import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import CasesRelacionados from '@/pages/cases/components/CasesRelacionados';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const processSteps = [
  { title: 'Inspeção Técnica', desc: 'Avaliação visual e dimensional do redutor para identificar condições gerais e pontos críticos de desgaste.' },
  { title: 'Diagnóstico de Falhas', desc: 'Análise técnica detalhada para identificar causas raiz de ruídos, vibração e perda de eficiência.' },
  { title: 'Desmontagem Técnica', desc: 'Desmontagem controlada com identificação, catalogação e avaliação de todas as engrenagens, eixos e rolamentos.' },
  { title: 'Análise de Componentes Internos', desc: 'Verificação dimensional e de integridade dos eixos, engrenagens, carcaças e elementos de vedação.' },
  { title: 'Avaliação de Engrenagens', desc: 'Análise de desgaste, microfissuras e integridade dos dentes e corpos das engrenagens helicoidais e cônicas.' },
  { title: 'Avaliação de Eixos e Rolamentos', desc: 'Verificação de folgas, desgaste e integridade dos eixos de transmissão e rolamentos de sustentação.' },
  { title: 'Recuperação de Componentes', desc: 'Recuperação de eixos, engrenagens, carcaças e demais peças com processos especializados e tratamentos térmicos.' },
  { title: 'Montagem', desc: 'Montagem técnica com ajustes de folgas, pré-carga de rolamentos e alinhamento conforme especificações do fabricante.' },
  { title: 'Testes Operacionais', desc: 'Validação em bancada de testes com medição de ruído, vibração, temperatura e funcionamento dinâmico em carga.' },
];

const problems = [
  'Ruídos Excessivos',
  'Vibração Anormal',
  'Superaquecimento',
  'Vazamentos de Óleo',
  'Desgaste de Engrenagens',
  'Falhas em Rolamentos',
  'Desalinhamento de Componentes',
  'Perda de Eficiência Operacional',
];

const integratedServices = [
  'Recuperação de Eixos',
  'Recuperação de Engrenagens',
  'Usinagens Especiais',
  'Ajustes Dimensionais',
  'Tratamentos Térmicos',
  'Tratamentos Superficiais',
];

const reducerTypes = [
  'Redutores Helicoidais',
  'Redutores Coaxiais',
  'Redutores de Eixos Paralelos',
  'Redutores Coroa e Rosca Sem Fim',
  'Motoredutores',
  'Redutores Industriais para Transportadores',
  'Redutores Industriais para Gruas',
  'Redutores Industriais para Equipamentos de Celulose',
];

const brands = [
  'SEW-Eurodrive',
  'WEG',
  'Bonfiglioli',
  'Nord',
  'Flender',
  'Sumitomo',
  'Falk',
  'Rossi',
  'Siemens',
  'Outras marcas sob consulta',
];

const segments = [
  'Papel e Celulose',
  'Mineração',
  'Portos',
  'Siderurgia',
  'Energia',
  'Construção Pesada',
];

const equipmentTypes = [
  'Transportadores Industriais',
  'Gruas Florestais',
  'Equipamentos de Movimentação de Cargas',
  'Equipamentos Portuários',
  'Equipamentos de Celulose',
  'Máquinas Industriais',
  'Sistemas de Transmissão Mecânica',
  'Equipamentos Industriais de Grande Porte',
];

export default function RecuperacaoRedutoresPage() {
  useSeo({
    title: 'Recuperação de Redutores Industriais | Oficina Especializada | Pilar',
    description: 'Recuperação de redutores industriais com diagnóstico técnico, desmontagem, recuperação de engrenagens e eixos, montagem e testes em bancada. Confiabilidade operacional para transportadores, gruas, equipamentos de celulose e sistemas de transmissão.',
    keywords: 'recuperação de redutores industriais, reparo de redutores, manutenção de redutores, recuperação de motoredutores, redutores helicoidais, redutores coaxiais, transmissão mecânica industrial, manutenção industrial, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/recuperacao-redutores-industriais',
    ogUrl: 'https://www.pilarmanutencao.com.br/recuperacao-redutores-industriais',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/OFICINA/RECUPERACAO-REDUTORES-INDUSTRIAIS/HERO/HERO-REDUTORES-INDUSTRIAIS.webp"
              alt="Recuperação de redutores industriais na oficina Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Oficina Especializada</span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Recuperação</span> de Redutores Industriais
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Recuperamos redutores industriais de diversos fabricantes e aplicações, atuando desde o diagnóstico técnico até a entrega final do equipamento pronto para operação.
              </p>
            </div>
          </div>
        </section>

        {/* O QUE FAZEMOS */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">O Que Fazemos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRINCIPAIS PROBLEMAS CORRIGIDOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Problemas Corrigidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{problem}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SOLUÇÃO COMPLETA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Solução Completa para Recuperação de Redutores</h2>
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                A Pilar coordina todas as etapas necessárias para recuperação de redutores industriais, garantindo qualidade, rastreabilidade e responsabilidade técnica centralizada durante todo o processo.
              </p>
              <p className="text-gray-400 leading-relaxed">
                O cliente recebe o equipamento pronto para retorno à operação, com acompanhamento técnico especializado.
              </p>
            </div>
          </div>
        </section>

        {/* SERVIÇOS ESPECIALIZADOS INTEGRADOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Serviços Especializados Integrados ao Processo</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Quando necessário, a Pilar coordena e acompanha tecnicamente processos especializados executados por parceiros homologados, garantindo qualidade e conformidade com os requisitos do projeto.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Esses serviços especializados são integrados ao processo de recuperação, garantindo que o cliente receba o equipamento completo e validado para retorno à operação.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {integratedServices.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300"
                  >
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TIPOS DE REDUTORES ATENDIDOS */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Tipos de Redutores Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reducerTypes.map((reducer, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-6-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{reducer}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MARCAS ATENDIDAS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Marcas Atendidas</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {brands.map((brand, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{brand}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENTOS ATENDIDOS */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EQUIPAMENTOS ATENDIDOS */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Equipamentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {equipmentTypes.map((eq, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-hammer-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{eq}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Atendimento Regional */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atendimento Regional no Espírito Santo</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[{ city: 'Aracruz', slug: 'aracruz', desc: 'Celulose, Florestal, Portos e Mineração' }, { city: 'Serra', slug: 'serra', desc: 'Siderurgia, Metal Mecânica e Mineração' }, { city: 'Vitória', slug: 'vitoria', desc: 'Portos, Logística e Mineração' }, { city: 'Linhares', slug: 'linhares', desc: 'Celulose, Florestal e Agroindústria' }].map((item) => (
                  <Link key={item.slug} to={`/recuperacao-redutores-industriais-${item.slug}`} className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-gp-yellow/30 p-5 bg-gp-graphite transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow transition-colors duration-300">
                        <i className="ri-map-pin-line text-gp-yellow group-hover:text-gp-graphite w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <span className="font-display text-sm font-bold text-white uppercase group-hover:text-gp-yellow transition-colors duration-300">{item.city}</span>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa recuperar um redutor industrial?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica especializada e receba um diagnóstico completo do seu equipamento.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Orçamento</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        <CasesRelacionados serviceSlugs={['recuperacao-redutores-industriais']} />

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}